import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlContainer, FormGroup, NgForm, FormBuilder, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Context } from 'ngx-global-nav';
import { CookieService as NgxCookieService } from 'ngx-shared-services';
import { Observable, Subscription, tap, combineLatest, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Commodity,
  Locale,
  LocaleBaseConfig,
  MultiLocaleComponent,
  MultiLocaleFormData,
  ServiceDeliveryPoint,
  statuses,
  Site,
  Timezone,
  Type,
  ServiceAccountCard,
  Utility,
  ExternalPointCard,
} from '../shared/model';
import { ServiceDeliveryPointFacade } from '../shared/facades/service-delivery-point.facade';
import { SdpFormValidators } from '../shared/validators/sdp-form.validators';

@Component({
  selector: 'app-service-delivery-point-form',
  templateUrl: './service-delivery-point-form.component.html',
  styleUrls: ['./service-delivery-point-form.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDeliveryPointFormComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  loadingExternalPoints$ = this.facade.loadingExternalPoints$;
  loadingServiceDeliveryPoint$ = this.facade.loadingServiceDeliveryPoint$;
  loadingOrgsObs$ = this.facade.loadingOrgs$;
  loadingSitesObs$ = this.facade.loadingSites$;

  externalPointsReady$ = this.facade.externalPointsReady$;

  public isAddExternalPointDisabled$ = this.facade.isAddExternalPointDisabled$;
  private addExternalPointDisabledLatest = true;

  public readonly EDIT = 'edit';
  public readonly CREATE = 'create';
  public dateError = false;
  public readonly APPPREFIX = 'uaa';

  public locales: Locale[];
  public timezones: Timezone[];
  public readonly statuses = statuses;

  public multiLocaleConfig: LocaleBaseConfig = {
    supportedLocales: [new Locale()],
    locales: [new Locale()],
    defaultLocale: new Locale(),
    id: '',
    displayLabelKey: 'displayLabel',
    localeKey: 'localeName',
  };
  trackByPoint = (_: number, externalPoint: ExternalPointCard): string => externalPoint.id ?? externalPoint.tempId!;

  public multidisplayLabels: Record<string, string>;
  public multiDescriptionDisplayLabels: Record<string, string>;
  public namePlaceholder = '';
  public descriptionPlaceholder = '';
  public nameLabel = '';
  public displayLabels = 'hola';
  public descriptionLabel = '';
  public userLocale = 'en_US';
  public commodities: Commodity[] = [];
  public types: Type[] = [];
  public isFirstTime = true;
  public sdp: ServiceDeliveryPoint;
  public firstTimePopup = true;
  public serviceAccounts: ServiceAccountCard[] = [];
  public externalPoints: ExternalPointCard[] = [];
  public utilities: Utility[] = [];
  public serviceAccountForms: Map<number, FormGroup> = new Map();
  public externalPointForms: Map<number, FormGroup> = new Map();
  serviceAccountFormGroup: FormArray<FormGroup> = new FormArray<FormGroup>([], {
    validators: [SdpFormValidators.uniqueAcross('ecrmId'), SdpFormValidators.overlappingDates()],
  });
  externalPointFormGroup: FormArray<FormGroup> = new FormArray<FormGroup>([], {
    validators: [SdpFormValidators.uniqueAcross('sourceId')],
  });

  @ViewChild('multiLocaleLabels') public multiLocaleDisplayLabels: MultiLocaleComponent;
  @ViewChild('multiDisplaylabels', { read: ElementRef }) public multiDisplaylabels: ElementRef<HTMLElement>;
  @ViewChild('multiLocaleDescriptionLabels') public multiLocaleDescriptionDisplayLabels: MultiLocaleComponent;
  @ViewChild('multiDescriptionlabels', { read: ElementRef }) public multiDescriptionlabels: ElementRef<HTMLElement>;
  @ViewChild('endDate', { read: ElementRef }) public endDate: ElementRef<HTMLElement>;
  @Input() public serviceDeliveryPointForm: FormGroup;
  @Input() public mode: string;
  public _serviceDeliveryPoint: ServiceDeliveryPoint;
  public selectedSite: string;
  public loadSite = true;

  public isEditMode = false;
  public isCreateMode = false;

  public readonly loadingSites$ = this.facade.loadingSites$;
  private loadSiteSubject = new BehaviorSubject(this.loadSite);
  private isFetchingExternalPoints = false;

  public readonly isLoading$ = combineLatest([this.facade.loadingSites$, this.loadSiteSubject]).pipe(
    map(([sitesLoading, localLoading]) => sitesLoading || localLoading),
  );
  dataProviders: any;
  channels: any[];
  loadingChannels: boolean;
  reportingIntervals: any[];
  isExternalPointsLoading: boolean;
  sdpDisplayLabelsByLocale: MultiLocaleFormData;

  public setLoadSite(loading: boolean): void {
    this.loadSite = loading;
    this.loadSiteSubject.next(loading);
  }

  public get sites(): Observable<Site[]> {
    return this.facade.sites$.pipe(
      tap((sites: Site[]) => {
        if (sites.length > 0) {
          const isSiteExist = sites
            .map((site: Site) => site.id)
            .includes(this.serviceDeliveryPointForm.get('siteId').value);
          if (!isSiteExist && sites.length <= 0) {
            this.serviceDeliveryPointForm.get('siteId').setValue('');
          }
        }
      }),
    );
  }

  public get loadingSDPs(): boolean {
    return this.facade.getServiceDeliveryPointsValue()?.isLoading || false;
  }

  public shouldShowLoader(): boolean {
    const isLoadingSites = this.facade.loadingSites$.getValue();
    const isLoadingOrgs = this.facade.loadingOrgs$.getValue();

    if (this.isCreateMode) {
      return isLoadingSites || isLoadingOrgs;
    }

    if (this.isEditMode) {
      return isLoadingOrgs || isLoadingSites || this.loadingSDPs || this.loadSite;
    }

    return isLoadingSites || isLoadingOrgs;
  }

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly translateService: TranslateService,
    private readonly ngxCookieService: NgxCookieService,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly facade: ServiceDeliveryPointFacade,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
  ) {
    this.timezones = this.facade.timezones;
    this.nameLabel = this.translateService.instant('utility.account.service_delivery_point_name');
    this.descriptionLabel = this.translateService.instant('utility.account.service_delivery_point_description');
    this.namePlaceholder = this.translateService.instant(
      'utility.account.create.placeholder.service_delivery_point_name',
    );
    this.descriptionPlaceholder = this.translateService.instant('utility.account.create.placeholder.description');
    this.userLocale = this.ngxCookieService.getCookie('locale') || 'en_US';

    this.setLoadSite(false);

    const addBtnSub = this.isAddExternalPointDisabled$.subscribe((disabled) => {
      this.addExternalPointDisabledLatest = disabled;
    });
    this.subscriptions.push(addBtnSub);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['mode'] && this.mode) {
      this.updateModeProperties();
    }
  }

  private updateModeProperties(): void {
    this.isEditMode = this.mode === this.EDIT;
    this.isCreateMode = this.mode === this.CREATE;
  }

  public async ngOnInit(): Promise<void> {
    await this.facade.initializeLookups();
    this.facade.setLoadingExternalPoints(false);
    if (this.isEditMode) {
      const sdpId = this.activatedRoute.snapshot.paramMap.get('id');
      if (sdpId && !this.facade.getServiceDeliveryPointValue()) {
        await this.facade.getServiceDeliveryPoint(sdpId);
      }

      const preSelectedSite = this.facade.getSelectedSite().getValue();
      if (preSelectedSite && sdpId && !this.serviceDeliveryPointForm.get('siteId').value) {
        this.serviceDeliveryPointForm.get('siteId').patchValue(preSelectedSite);
      }
    }
    await this.initializeSubscriptions();
    await this.loadExternalPointsForCurrentContext();
    this.triggerValidation();
  }
  async initializeSubscriptions(): Promise<void> {
    const dataProvidersSub = this.facade.dataProviders$.subscribe((dataProviders) => {
      if (dataProviders) {
        this.dataProviders = dataProviders;
        console.debug('ServiceDeliveryPointForm: dataProviders set', this.dataProviders);
      }
    });

    const channelsSub = this.facade.channels$.subscribe((channels) => {
      console.debug('ServiceDeliveryPointForm: channels$ emitted', channels);
      if (Array.isArray(channels) && channels.length > 0) {
        // get filtered channels from service
        const filtered = this.facade.getExternalPointsChannels();
        console.debug('ServiceDeliveryPointForm: filtered channels', filtered);
        this.channels = filtered;
        this.loadingChannels = false;
      }
    });
    const reportingIntervalsSub = this.facade.reportingIntervals$.subscribe((reportingIntervals) => {
      reportingIntervals.sort((a: any, b: any) => {
        if (parseInt(a.id, 10) < parseInt(b.id, 10)) {
          return -1;
        } else if (parseInt(a.id, 10) > parseInt(b.id, 10)) {
          return 1;
        } else {
          return 0;
        }
      });
      this.reportingIntervals = reportingIntervals;
    });
    const selectedSiteSub = this.facade.getSelectedSite().subscribe((selectedSite: string) => {
      if (selectedSite && this.isFirstTime) {
        this.serviceDeliveryPointForm.get('siteId').setValue(selectedSite);
        this.isFirstTime = false;
      }

      if (this.isEditMode) {
        if (selectedSite) {
          void this.loadExternalPointsForCurrentContext();
        } else if (this.facade.getLoadingExternalPointsValue()) {
          this.facade.setLoadingExternalPoints(false);
        }
      }
    });
    const typesSub = this.facade.types$.subscribe((types: any) => {
      if (types) {
        this.types = Object.keys(types).map((k: string) => types[k]);
      }
    });

    const commoditiesSub = this.facade.commodities$.subscribe((commodities: any) => {
      if (Array.isArray(commodities) && commodities.length > 0) {
        this.commodities = commodities;
      }
    });

    const localeServiceSub = this.facade.locales$.subscribe((locales: Locale[]) => {
      this.locales = locales;
      const locale = this.facade.checkForLocale(this.userLocale, locales) || locales[0];
      if (this.isCreateMode) {
        this.multiLocaleConfig.defaultLocale = locale;
        this.serviceDeliveryPointForm.get('defaultLocale').patchValue(locale.localeName);
        this.serviceDeliveryPointForm.get('supportedLocales').patchValue([locale]);
      } else if (this.isEditMode) {
        this.multiLocaleConfig.defaultLocale = locale;
        this.buildSupportedLocales();
      }
    });
    try {
      const utilitiesSub = this.facade.utilities$.subscribe((utilities: Utility[]) => {
        if (utilities && utilities.length > 0) {
          this.utilities = utilities;
        }
      });
      this.subscriptions.push(utilitiesSub);
    } catch (error) {
      console.error('Error loading utilities:', error);
      this.utilities = [];
    }

    if (this.isEditMode) {
      this.isFirstTime = false;
      const sdpSub = this.facade.serviceDeliveryPoint$.subscribe(async (serviceDeliveryPoint: ServiceDeliveryPoint) => {
        if (serviceDeliveryPoint) {
          this.sdp = serviceDeliveryPoint;
          this.multiDescriptionDisplayLabels = serviceDeliveryPoint.descriptions;
          this.multidisplayLabels = serviceDeliveryPoint.displayLabels;
          this.buildSupportedLocales();

          this.sdpDisplayLabelsByLocale = { ...(serviceDeliveryPoint.displayLabels ?? {}) };
          this.cdr.markForCheck();

          await this.loadExternalPointsForCurrentContext();
        }
      });

      const serviceAccountSub = this.facade.serviceAccounts$.subscribe((accounts) => {
        if (accounts && accounts.length > 0) {
          const accs = this.facade.mapServiceAccountsToCards(accounts);
          this.serviceAccounts = accs.sort((a, b) => {
            return new Date(b.startDate || 0).getTime() - new Date(a.startDate || 0).getTime();
          });
        }
      });

      const orgSelectorSub = this.facade.currentContext$.subscribe(async (orgs: Context[]) => {
        const org = orgs[0];
        if (this.sdp && org && this.serviceDeliveryPointForm.get('siteId').value === '') {
          this.setLoadSite(true);
          try {
            const siteId = org.id ? await this.facade.getParentSiteId(org.id, this.sdp.id) : undefined;
            if (siteId) {
              this.serviceDeliveryPointForm.get('siteId').patchValue(siteId);
              this.facade.setSdpParentId(siteId);
              this.facade.setSelectedSite(siteId);
              this.facade.selectSite(siteId);
              await this.loadExternalPointsForCurrentContext();
            } else {
              if (this.facade.getLoadingExternalPointsValue()) {
                this.facade.setLoadingExternalPoints(false);
              }
              this.facade.setExternalPoints([]);
            }
          } catch (error) {
            console.error('Error resolving parent site for SDP', error);
            if (this.facade.getLoadingExternalPointsValue()) {
              this.facade.setLoadingExternalPoints(false);
            }
          } finally {
            this.setLoadSite(false);
          }
        }
      });

      const externalPointsSub = this.facade.externalPoints$.subscribe((externalPoints) => {
        if (externalPoints) {
          this.externalPoints = externalPoints;
        }
      });

      this.subscriptions.push(...[sdpSub, serviceAccountSub, orgSelectorSub, externalPointsSub]);
    }
    this.subscriptions.push(
      ...[
        typesSub,
        commoditiesSub,
        localeServiceSub,
        selectedSiteSub,
        dataProvidersSub,
        channelsSub,
        reportingIntervalsSub,
      ],
    );
  }

  public isEdit(): boolean {
    return this.router.url.includes('edit');
  }

  public ngAfterViewInit(): void {
    this.facade.loadingSites$.subscribe((loading: boolean) => {
      if (loading) {
        this.serviceDeliveryPointForm.disable();
        this.multiDisplaylabels.nativeElement.querySelector('input').disabled = true;
        this.multiDescriptionlabels.nativeElement.querySelector('input').disabled = true;
      } else {
        this.serviceDeliveryPointForm.enable();
        if (this.isEditMode) {
          this.serviceDeliveryPointForm.get('sdpType').disable();
        }
        this.multiDisplaylabels.nativeElement.querySelector('input').disabled = false;
        this.multiDescriptionlabels.nativeElement.querySelector('input').disabled = false;
      }
    });
    if (this.isCreateMode) {
      this.multiLocaleDisplayLabels.form.valueChanges.subscribe((multilocales: MultiLocaleFormData) => {
        const newValue = this.transformMultiLocaleValue(multilocales.localeInputsForm_sdp_display_labels);
        const status = this.multiLocaleDisplayLabels.form.status;
        this.updateDisplayLabels(status, newValue);
        this.multiDisplaylabels.nativeElement.querySelector('input').disabled =
          this.endDate.nativeElement.querySelector('input').disabled;
      });
    } else if (this.isEditMode) {
      this.multiLocaleDisplayLabels.form.valueChanges.subscribe((multilocales: MultiLocaleFormData) => {
        const newValue = this.transformMultiLocaleValue(multilocales.localeInputsForm_sdp_display_labels);
        const status = this.multiLocaleDisplayLabels.form.status;
        if (status === 'INVALID') {
          this.serviceDeliveryPointForm.get('displayLabels').setErrors({ required: true });
        } else {
          this.serviceDeliveryPointForm.get('displayLabels').setErrors(null);
        }
        this.sdpDisplayLabelsByLocale = { ...newValue };
        this.cdr.markForCheck();

        this.multiDisplaylabels.nativeElement.querySelector('input').disabled =
          this.endDate.nativeElement.querySelector('input').disabled;
      });
    }
    const multilocalesDescriptionSub = this.multiLocaleDescriptionDisplayLabels.form.valueChanges.subscribe(
      (multilocales: MultiLocaleFormData) => {
        const newValue = this.transformMultiLocaleDescriptionValue(
          multilocales.localeInputsForm_sdp_description_display_labels,
        );
        const status = this.multiLocaleDescriptionDisplayLabels.form.status;
        this.updateDescriptionDisplayLabels(status, newValue);
        this.multiDescriptionlabels.nativeElement.querySelector('input').disabled =
          this.endDate.nativeElement.querySelector('input').disabled;
      },
    );
    this.subscriptions.push(multilocalesDescriptionSub);
  }

  public transformMultiLocaleValue(multilocales: Record<string, string>): Record<string, string> {
    const displayLabels: Record<string, string> = {};
    if (multilocales) {
      const locales = Object.keys(multilocales);
      if (locales) {
        for (const locale of locales) {
          const key = locale
            .replace('sdp_display_labels_', '')
            .split('_')
            .map((item: string, i: number) => (i === 1 ? item.toUpperCase() : item))
            .join('_');
          displayLabels[key] = multilocales[locale];
        }
      }
    }
    return displayLabels;
  }

  public transformMultiLocaleDescriptionValue(multilocales: Record<string, string>): Record<string, string> {
    const descriptions: Record<string, string> = {};
    if (multilocales) {
      const locales = Object.keys(multilocales);
      if (locales) {
        for (const locale of locales) {
          const key = locale
            .replace('sdp_description_display_labels_', '')
            .split('_')
            .map((item: string, i: number) => (i === 1 ? item.toUpperCase() : item))
            .join('_');
          descriptions[key] = multilocales[locale];
        }
      }
    }
    return descriptions;
  }

  public updateDisplayLabels(status: string, newValue: Record<string, string>): void {
    this.serviceDeliveryPointForm.get('displayLabels').setValue({ ...newValue });
    this.serviceDeliveryPointForm.get('displayLabels').markAsTouched();
    if (status === 'INVALID') {
      this.serviceDeliveryPointForm.get('displayLabels').setErrors({ required: true });
    } else {
      this.serviceDeliveryPointForm.get('displayLabels').setErrors(null);
    }
  }

  public updateDescriptionDisplayLabels(status: string, newValue: Record<string, string>): void {
    this.serviceDeliveryPointForm.get('descriptions').setValue({ ...newValue });
    this.serviceDeliveryPointForm.get('descriptions').markAsTouched();

    this.serviceDeliveryPointForm.get('descriptions').setErrors(null);
    this.serviceDeliveryPointForm.get('descriptions').updateValueAndValidity();
  }

  public limitLocaleSelectList(num: number, locale: Locale): boolean {
    return (
      this.serviceDeliveryPointForm.get('supportedLocales').value.length > num - 1 &&
      !this.serviceDeliveryPointForm.get('supportedLocales').value.includes(locale)
    );
  }

  public onlyLocaleSelected(locale: Locale): boolean {
    return (
      this.serviceDeliveryPointForm.get('supportedLocales').value.length === 1 &&
      this.serviceDeliveryPointForm.get('supportedLocales').value[0] === locale
    );
  }

  public handleSupportedLocalesSelection(): void {
    this.triggerValidation();
    const selectedLocales = this.serviceDeliveryPointForm
      .get('supportedLocales')
      .value.map((locale: Locale) => locale.localeName);
    if (!selectedLocales.includes(this.serviceDeliveryPointForm.get('defaultLocale').value)) {
      this.serviceDeliveryPointForm.get('defaultLocale').setValue(selectedLocales[0]);
    }
    this.multiDisplaylabels.nativeElement.querySelector('input').focus();
    this.multiDisplaylabels.nativeElement.querySelector('input').blur();
  }

  public buildSupportedLocales(): void {
    if (
      this.serviceDeliveryPointForm.get('supportedLocales').value &&
      this.serviceDeliveryPointForm.get('displayLabels').value &&
      this.locales
    ) {
      this.serviceDeliveryPointForm.get('supportedLocales').setValue([]);
      this.multiLocaleConfig.supportedLocales = [];
      const keys = Object.keys(this.serviceDeliveryPointForm.get('displayLabels').value);
      for (const key of keys) {
        const localeFound = this.locales.find((locale: Locale) => locale.localeName === key);
        if (localeFound) {
          this.serviceDeliveryPointForm
            .get('supportedLocales')
            .patchValue([localeFound, ...this.serviceDeliveryPointForm.get('supportedLocales').value]);
        }
      }
    }

    if (
      this.serviceDeliveryPointForm.get('supportedLocales').value &&
      this.serviceDeliveryPointForm.get('descriptions').value &&
      this.locales
    ) {
      const descriptionKeys = Object.keys(this.serviceDeliveryPointForm.get('descriptions').value);
      for (const key of descriptionKeys) {
        const localeFound = this.locales.find((locale: Locale) => locale.localeName === key);
        if (localeFound && !this.serviceDeliveryPointForm.get('supportedLocales').value.includes(localeFound)) {
          this.serviceDeliveryPointForm
            .get('supportedLocales')
            .patchValue([localeFound, ...this.serviceDeliveryPointForm.get('supportedLocales').value]);
        }
      }
    }
  }

  public triggerValidation(): void {
    this.serviceDeliveryPointForm.get('displayLabels').markAsTouched();
    this.serviceDeliveryPointForm.get('displayLabels').updateValueAndValidity();

    this.serviceDeliveryPointForm.get('descriptions').markAsTouched();
    this.serviceDeliveryPointForm.get('descriptions').setErrors(null);
    this.serviceDeliveryPointForm.get('descriptions').updateValueAndValidity();
  }

  public getMinDate(): Date {
    const minDate = new Date(this.serviceDeliveryPointForm.get('startDttm').value);
    minDate.setDate(minDate.getDate() + 1);
    return minDate;
  }

  private async loadExternalPointsForCurrentContext(): Promise<void> {
    const siteId = this.serviceDeliveryPointForm.get('siteId').value || this.facade.getSelectedSite().getValue();

    if (!this.sdp?.id || !siteId) {
      if (this.facade.getLoadingExternalPointsValue()) {
        this.facade.setLoadingExternalPoints(false);
      }
      return;
    }

    if (this.isFetchingExternalPoints) {
      return;
    }

    this.isFetchingExternalPoints = true;

    try {
      await this.facade.getExternalPoints(siteId, 'SITE', this.sdp.id);
    } catch (error) {
      console.error('Error loading external points for SDP', error);
      this.facade.setLoadingExternalPoints(false);
    } finally {
      this.isFetchingExternalPoints = false;
    }
  }

  public checkDates(): void {
    setTimeout(() => {
      this.endDate.nativeElement.querySelector('input').focus();
      this.endDate.nativeElement.querySelector('input').blur();
    }, 50);
    this.dateError =
      new Date(this.serviceDeliveryPointForm.get('endDttm').value) <
      new Date(this.serviceDeliveryPointForm.get('startDttm').value);
  }

  public supportedLocalesMenuToggled(event: boolean): void {
    if (!event) {
      this.multiDisplaylabels.nativeElement.querySelector('input').focus();
      this.multiDisplaylabels.nativeElement.querySelector('input').blur();
    }
  }

  public addServiceAccount(): void {
    const newServiceAccount: ServiceAccountCard = {
      id: null,
      serviceAccountNumber: '',
      status: 'INACTIVE',
      expanded: true,
      form: this.formBuilder.group({}),
    };

    this.serviceAccounts.unshift(newServiceAccount);
  }
  private generateFallbackId(): string {
    return 'temp-' + Math.random().toString(36).substring(2, 11);
  }

  public addExternalPoint(): void {
    if (this.addExternalPointDisabledLatest) return;
    const newExternalPoint: ExternalPointCard = {
      id: null,
      tempId: window.crypto?.randomUUID?.() ?? this.generateFallbackId(),
      displayLabels: null,
      description: '',
      dataproviderId: '',
      channelId: '',
      reportingInterval: '',
      alternateIds: null,
      expressionFactor: 1,
      veeBypass: true,
      expanded: true,
    };
    this.externalPoints = [newExternalPoint, ...this.externalPoints];
    this.cdr.markForCheck();
  }

  public get localeConfig(): LocaleBaseConfig {
    return this.multiLocaleConfig;
  }

  public toggleServiceAccount(index: number): void {
    this.serviceAccounts[index].expanded = !this.serviceAccounts[index].expanded;
  }

  public toggleExternalPoint(index: number): void {
    this.externalPoints[index].expanded = !this.externalPoints[index].expanded;
  }

  public removeServiceAccount(index: number): void {
    this.serviceAccounts.splice(index, 1);
    this.serviceAccountForms.delete(index);

    const newForms = new Map<number, FormGroup>();
    this.serviceAccountForms.forEach((form, oldIndex) => {
      if (oldIndex > index) {
        newForms.set(oldIndex - 1, form);
      } else if (oldIndex < index) {
        newForms.set(oldIndex, form);
      }
    });
    this.serviceAccountForms = newForms;
    this.serviceAccountFormGroup.removeAt(index);
  }

  public removeExternalPoint(index: number): void {
    this.externalPoints.splice(index, 1);
    this.externalPointForms.delete(index);

    const newForms = new Map<number, FormGroup>();
    this.externalPointForms.forEach((form, oldIndex) => {
      if (oldIndex > index) {
        newForms.set(oldIndex - 1, form);
      } else if (oldIndex < index) {
        newForms.set(oldIndex, form);
      }
    });
    this.externalPointForms = newForms;
    this.externalPointFormGroup.removeAt(index);
  }

  public onServiceAccountFormChange(event: { index: number; form: FormGroup }): void {
    if (!(event.form instanceof FormGroup)) return;
    this.serviceAccountForms.set(event.index, event.form);
    this.serviceAccountFormGroup.setControl(event.index, event.form);
    this.serviceAccountFormGroup.updateValueAndValidity();
  }
  public onExternalPointFormChange(event: { index: number; form: FormGroup }): void {
    if (!(event.form instanceof FormGroup)) return;
    this.externalPointForms.set(event.index, event.form);
    this.externalPointFormGroup.setControl(event.index, event.form);
    this.externalPointFormGroup.updateValueAndValidity();
  }

  public areServiceAccountsValid(): boolean {
    let allValid = true;
    this.serviceAccountForms.forEach((form) => {
      if (!form.valid) {
        allValid = false;
      }
    });
    return allValid;
  }

  public areExternalPointsValid(): boolean {
    let allValid = true;
    this.externalPointFormGroup.controls.forEach((form) => {
      if (!form.valid) {
        allValid = false;
      }
    });
    return allValid;
  }
  public getServiceAccount(index: number): ServiceAccountCard | undefined {
    return this.serviceAccounts[index];
  }

  public getServiceAccountForm(index: number): FormGroup | undefined {
    return this.serviceAccountForms.get(index);
  }

  public flagExternalPointError(index: number): void {
    if (index < 0 || index >= this.externalPoints.length) return;

    const point = this.externalPoints[index];
    const updatedPoint = { ...point, hasServerError: true };

    this.externalPoints = this.externalPoints.map((point, idx) => (idx === index ? updatedPoint : point));
    this.cdr.markForCheck();
  }

  public flagServiceAccountError(index: number): void {
    if (index < 0 || index >= this.serviceAccounts.length) return;
    const account = this.serviceAccounts[index];
    const updatedAccount = { ...account, hasServerError: true, expanded: true };
    this.serviceAccounts = this.serviceAccounts.map((acc, idx) => (idx === index ? updatedAccount : acc));

    this.cdr.markForCheck();
  }

  public clearExternalPointError(index: number): void {
    if (index < 0 || index >= this.externalPoints.length) return;

    const point = this.externalPoints[index];
    if (!point?.hasServerError) return;

    const updatedPoint = { ...point, hasServerError: false };
    this.externalPoints = this.externalPoints.map((candidate, idx) => (idx === index ? updatedPoint : candidate));

    this.cdr.markForCheck();
  }

  public clearServiceAccountError(index: number): void {
    if (index < 0 || index >= this.serviceAccounts.length) return;
    const account = this.serviceAccounts[index];
    if (!account?.hasServerError) return;
    const updatedAccount = { ...account, hasServerError: false, expanded: false };
    this.serviceAccounts = this.serviceAccounts.map((acc, idx) => (idx === index ? updatedAccount : acc));

    this.cdr.markForCheck();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }
}
